/**
 * ☰ › Groups › **Request a group…** — anyone asks for a group, and sees what
 * became of what they asked (`docs/plans/group-requests.md` §1, the approved
 * text mock of 2026-10-12):
 *
 *   Request a group
 *     Name                  [                                    ]
 *     What it's for         [ why this group should exist                 ]
 *     What's unique to it   [ screens, layouts, indicators, data…          ]
 *     How you'd sell it     (•) QuFolio sells it   ( ) We sell it ourselves
 *     Expected members      [    ]
 *     Email to discuss this [ ana.business@example.com ]
 *     Phone (optional)      [                    ]
 *                                                      [ Send request ]
 *
 * **The email is the one the deal is done on**, not necessarily the one they
 * sign in with — a personal sign-in, a business address for the club. So it is
 * asked for, required, and pre-filled with the sign-in email *only when the
 * provider says what that is*; an empty box is honest where an invented one is
 * a conversation sent to nobody. It is taken as typed: the shape is checked
 * here and on the server, nothing more.
 *
 * The Group Request Terms tick is step 2 and waits for the owner's words; it is
 * deliberately not drawn, rather than drawn linking to nothing.
 *
 * **Your requests** sits under the form so the answer is where the question was
 * asked. Nothing is emailed: this list is how a requester learns they were
 * declined, and why, or which group key they now administer.
 */

import { useCallback, useEffect, useState } from "react";

import {
  createGroupRequest,
  myGroupRequests,
  type GroupRequest,
  type SellAs,
} from "../api/groupRequests";
import { signedInEmail } from "../lib/session";

/** The server's caps, mirrored so a long paste is cut where it would be refused. */
const NAME_MAX = 80;
const TEXT_MAX = 4000;
const PHONE_MAX = 40;

/** A shape check only — the address is not verified (owner, 2026-10-12). */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PANEL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  width: 520,
  maxWidth: "92vw",
  maxHeight: "75vh",
  overflowY: "auto",
  color: "var(--qf-text)",
  fontSize: 13,
};

const GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  gap: "6px 10px",
  alignItems: "start",
};

const LABEL: React.CSSProperties = { color: "var(--qf-text-secondary)", paddingTop: 3 };

const INPUT: React.CSSProperties = {
  font: "inherit",
  padding: "3px 6px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  minWidth: 0,
};

const AREA: React.CSSProperties = { ...INPUT, minHeight: 56, resize: "vertical" };

const BUTTON: React.CSSProperties = {
  font: "inherit",
  padding: "3px 12px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const SECTION: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
  margin: "14px 0 4px",
};

const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative)" };
const DONE: React.CSSProperties = { ...NOTE, color: "var(--qf-positive)" };

const ROW: React.CSSProperties = {
  padding: "6px 0",
  borderTop: "1px solid var(--qf-border)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const STATUS_WORD: Record<GroupRequest["status"], string> = {
  pending: "Pending — the owner will be in touch",
  approved: "Approved",
  declined: "Declined",
};

export function GroupRequestForm() {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [uniqueFeatures, setUniqueFeatures] = useState("");
  const [sellAs, setSellAs] = useState<SellAs>("q");
  const [members, setMembers] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [mine, setMine] = useState<GroupRequest[] | null>(null);

  const reload = useCallback(() => {
    void myGroupRequests().then(setMine);
  }, []);
  useEffect(reload, [reload]);

  // Pre-fill once, and never over something already typed.
  useEffect(() => {
    let alive = true;
    void signedInEmail().then((address) => {
      if (alive && address) setEmail((typed) => typed || address);
    });
    return () => {
      alive = false;
    };
  }, []);

  const trimmedEmail = email.trim();
  const membersValue = members.trim() === "" ? null : Number(members);
  const membersOk =
    membersValue === null || (Number.isInteger(membersValue) && membersValue >= 0);
  const missing =
    !name.trim() || !purpose.trim() || !uniqueFeatures.trim() || !trimmedEmail;
  const emailOk = trimmedEmail === "" || EMAIL_SHAPE.test(trimmedEmail);
  const canSend = !busy && !missing && emailOk && membersOk;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      await createGroupRequest({
        name: name.trim(),
        purpose: purpose.trim(),
        uniqueFeatures: uniqueFeatures.trim(),
        sellAs,
        expectedMembers: membersValue,
        contactEmail: trimmedEmail,
        contactPhone: phone.trim() || null,
      });
      setName("");
      setPurpose("");
      setUniqueFeatures("");
      setMembers("");
      setPhone("");
      setSent(true);
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={PANEL}>
      <div style={GRID}>
        <label style={LABEL} htmlFor="gr-name">Name</label>
        <input
          id="gr-name"
          style={INPUT}
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => setName(event.target.value)}
        />

        <label style={LABEL} htmlFor="gr-purpose">What it's for</label>
        <textarea
          id="gr-purpose"
          style={AREA}
          value={purpose}
          maxLength={TEXT_MAX}
          placeholder="why this group should exist"
          onChange={(event) => setPurpose(event.target.value)}
        />

        <label style={LABEL} htmlFor="gr-unique">What's unique to it</label>
        <textarea
          id="gr-unique"
          style={AREA}
          value={uniqueFeatures}
          maxLength={TEXT_MAX}
          placeholder="screens, layouts, indicators, data…"
          onChange={(event) => setUniqueFeatures(event.target.value)}
        />

        <span style={LABEL}>How you'd sell it</span>
        <div role="radiogroup" aria-label="How you'd sell it" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <label>
            <input
              type="radio"
              name="gr-sell"
              checked={sellAs === "q"}
              onChange={() => setSellAs("q")}
            />{" "}
            QuFolio sells it
          </label>
          <label>
            <input
              type="radio"
              name="gr-sell"
              checked={sellAs === "o"}
              onChange={() => setSellAs("o")}
            />{" "}
            We sell it ourselves
          </label>
        </div>

        <label style={LABEL} htmlFor="gr-members">Expected members</label>
        <input
          id="gr-members"
          style={{ ...INPUT, width: 80 }}
          inputMode="numeric"
          value={members}
          onChange={(event) => setMembers(event.target.value)}
        />

        <label style={LABEL} htmlFor="gr-email">Email to discuss this</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <input
            id="gr-email"
            type="email"
            style={INPUT}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <span style={NOTE}>Required — where the owner will write to you about it.</span>
        </div>

        <label style={LABEL} htmlFor="gr-phone">Phone (optional)</label>
        <input
          id="gr-phone"
          type="tel"
          style={{ ...INPUT, width: 200 }}
          value={phone}
          maxLength={PHONE_MAX}
          onChange={(event) => setPhone(event.target.value)}
        />
      </div>

      {!emailOk && <div style={ERROR}>That email does not look like an address.</div>}
      {!membersOk && <div style={ERROR}>Expected members is a whole number.</div>}
      {error && <div style={ERROR}>{error}</div>}
      {sent && <div style={DONE}>Sent. The owner will write to you at that email.</div>}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          style={{ ...BUTTON, opacity: canSend ? 1 : 0.5 }}
          disabled={!canSend}
          onClick={() => void send()}
        >
          {busy ? "Sending…" : "Send request"}
        </button>
      </div>

      <div style={SECTION}>Your requests</div>
      {mine === null ? null : mine.length === 0 ? (
        <div style={NOTE}>None yet.</div>
      ) : (
        mine.map((request) => (
          <div key={request.key} style={ROW} data-testid="my-group-request">
            <div>
              <strong>{request.name}</strong>{" "}
              <span style={NOTE}>
                {request.sellAs === "q" ? "QuFolio sells it" : "you sell it"}
                {" · "}
                {request.createdAt.slice(0, 10)}
              </span>
            </div>
            <div style={request.status === "declined" ? ERROR : request.status === "approved" ? DONE : NOTE}>
              {STATUS_WORD[request.status]}
              {request.status === "approved" && request.orgKey && (
                <>
                  {" — group "}
                  <code>{request.orgKey}</code>, you are its Group Admin
                </>
              )}
            </div>
            {request.status === "declined" && request.declineReason && (
              <div style={NOTE}>Reason: {request.declineReason}</div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
